import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CheckIcon, Plus, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/custom/Dialog';
import FieldRow, { FormField } from './FieldRow';

// Define field types available for form templates
const fieldTypes = [
  { value: 'text', label: 'Text Field' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'date', label: 'Date Picker' },
  { value: 'number', label: 'Number Input' }
];

// Interface for the form template structure
type FormTemplate = {
  id?: number; // Make id optional for new templates
  name: string;
  description: string; // Make description required for the builder
  schema: FormField[]; // Ensure schema is always FormField[] in the builder
  created_at?: string; // Optional for compatibility with imported type
  updated_at?: string; // Optional for compatibility with imported type
};

// Props for the component
interface FormTemplateBuilderProps {
  onSave?: (template: FormTemplate) => void;
  editMode?: boolean;
  initialTemplate?: FormTemplate;
}

const FormTemplateBuilder: React.FC<FormTemplateBuilderProps> = ({
  onSave,
  editMode = false,
  initialTemplate
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { showToast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor));

  // State for the form template being edited
  const [template, setTemplate] = useState<FormTemplate>({
    name: '',
    description: '',
    schema: []
  });

  // State for the current field being edited
  const [currentField, setCurrentField] = useState<FormField>({
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: []
  });

  // sort columns
  const sortColumn = (event: DragEndEvent) => {
    if (!template.schema.length) {
      return;
    }
    const { active, over } = event;

    if (active.id !== over?.id) {
      let schemas = [...template.schema];

      const oldIndex = schemas.findIndex((input) => input.name === active.id);
      const newIndex = schemas.findIndex((input) => input.name === over?.id);

      schemas = arrayMove(schemas, oldIndex, newIndex);
      setTemplate((prev) => ({
        ...prev,
        schema: schemas
      }));
    }
  };

  // State for the current option being added to a select/radio/checkbox field
  const [currentOption, setCurrentOption] = useState({ label: '', value: '' });

  // State to track which field is being edited
  const [editingFieldIndex, setEditingFieldIndex] = useState<number | null>(null);

  // State for the modal visibility
  const [isModalVisible, setIsModalVisible] = useState(false);

  // State for active tab
  const [activeTab, setActiveTab] = useState<string>('basic');

  // Load initial template if in edit mode or reset form if not in edit mode
  useEffect(() => {
    if (editMode && initialTemplate) {
      setTemplate(initialTemplate);
    } else {
      setTemplate({
        name: '',
        description: '',
        schema: []
      });
    }
  }, [editMode, initialTemplate]);

  // Handle changes to the template basic info
  const handleTemplateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTemplate((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle changes to the current option being added
  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentOption((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle field changes from FieldRow component
  const handleFieldRowChange = (index: number, updatedField: FormField) => {
    setTemplate((prev) => {
      const updatedSchema = [...prev.schema];
      updatedSchema[index] = updatedField;
      return { ...prev, schema: updatedSchema };
    });
  };

  // Add a new option to the current field
  const addOption = () => {
    if (!currentOption.label || !currentOption.value) {
      showToast('Both label and value are required for options', 'error');
      return;
    }

    setCurrentField((prev) => {
      // Ensure options is an array and add the new option
      const currentOptions = Array.isArray(prev.options) ? [...prev.options] : [];
      const newOption =
        currentOption.label === currentOption.value
          ? currentOption.value // Use string if label and value are the same
          : { ...currentOption }; // Use object if they're different

      return {
        ...prev,
        options: [...currentOptions, newOption]
      } as FormField; // Cast to FormField to satisfy TypeScript
    });

    // Reset the current option
    setCurrentOption({ label: '', value: '' });
  };

  // Remove an option from the current field
  const removeOption = (index: number) => {
    setCurrentField(
      (prev) =>
        ({
          ...prev,
          options: prev.options?.filter((_, i) => i !== index) || []
        }) as FormField
    );
  };

  // Handle changes to the current field being edited
  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    // If field type changes to/from select/radio/checkbox, handle options appropriately
    if (name === 'type') {
      setCurrentField((prev) => {
        const newField = {
          ...prev,
          [name]: value
        } as FormField;

        // Only include options for field types that need them
        if (['select', 'radio', 'checkbox'].includes(value)) {
          newField.options = Array.isArray(prev.options) ? [...prev.options] : [];
        } else if ('options' in newField) {
          delete newField.options;
        }

        return newField;
      });
    } else {
      setCurrentField(
        (prev) =>
          ({
            ...prev,
            [name]: checked !== undefined ? checked : value
          }) as FormField
      );
    }
  };

  // Save the current field being edited
  const handleSaveField = () => {
    // Validate field
    if (!currentField.name || !currentField.label) {
      showToast('Field name and label are required', 'error');
      return;
    }

    // Check for duplicate field names
    const isDuplicate = template.schema.some(
      (field, index) => field.name === currentField.name && index !== editingFieldIndex
    );

    if (isDuplicate) {
      showToast('Field name must be unique', 'error');
      return;
    }

    // Validate options for select/radio/checkbox fields
    if (['select', 'radio', 'checkbox'].includes(currentField.type)) {
      if (!currentField.options || currentField.options.length === 0) {
        showToast(`Please add at least one option for ${currentField.type} field`, 'error');
        return;
      }

      // Ensure all options are valid
      const hasInvalidOptions = currentField.options.some((option) => {
        if (typeof option === 'string') return false; // String options are always valid
        return !option.value || !option.label;
      });

      if (hasInvalidOptions) {
        showToast('All options must have both a label and a value', 'error');
        return;
      }
    }

    // Update the template with the new/edited field
    const updatedSchema = [...template.schema];

    // Create a clean field object with only the necessary properties
    const cleanField: FormField = {
      name: currentField.name,
      label: currentField.label,
      type: currentField.type,
      required: currentField.required || false
    };

    // Only include options for field types that need them
    if (['select', 'radio', 'checkbox'].includes(currentField.type)) {
      cleanField.options = currentField.options || [];
    }

    if (editingFieldIndex !== null) {
      updatedSchema[editingFieldIndex] = cleanField;
    } else {
      updatedSchema.push(cleanField);
    }

    setTemplate((prev) => ({
      ...prev,
      schema: updatedSchema
    }));

    // Reset current field and editing state
    setCurrentField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: []
    });
    setEditingFieldIndex(null);
    setIsModalVisible(false);
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setCurrentField({
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: []
    });
    setEditingFieldIndex(null);
    setIsModalVisible(false);
  };

  // Open modal to add a new field
  const handleAddField = () => {
    setTemplate((prev) => ({
      ...prev,
      schema: [
        ...prev.schema,
        {
          name: 'input-' + (prev.schema.length + 1),
          label: 'Input ' + (prev.schema.length + 1),
          type: 'text',
          required: false
        }
      ]
    }));
  };

  // Open modal to edit an existing field
  const handleEditField = (index: number) => {
    const fieldToEdit = template.schema[index];
    setCurrentField({
      name: fieldToEdit.name,
      label: fieldToEdit.label,
      type: fieldToEdit.type,
      required: fieldToEdit.required || false,
      options: fieldToEdit.options
        ? [...fieldToEdit.options] // Create a shallow copy of the options array
        : []
    });
    setEditingFieldIndex(index);
    setIsModalVisible(true);
  };

  // Delete a field
  const handleDeleteField = (index: number) => {
    const updatedSchema = template.schema.filter((_, i) => i !== index);
    setTemplate((prev) => ({
      ...prev,
      schema: updatedSchema
    }));
  };

  // Move a field up in the order
  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const updatedSchema = [...template.schema];
    const temp = updatedSchema[index];
    updatedSchema[index] = updatedSchema[index - 1];
    updatedSchema[index - 1] = temp;

    setTemplate((prev) => ({
      ...prev,
      schema: updatedSchema
    }));
  };

  // Move a field down in the order
  const handleMoveDown = (index: number) => {
    if (index === template.schema.length - 1) return;

    const updatedSchema = [...template.schema];
    const temp = updatedSchema[index];
    updatedSchema[index] = updatedSchema[index + 1];
    updatedSchema[index + 1] = temp;

    setTemplate((prev) => ({
      ...prev,
      schema: updatedSchema
    }));
  };

  // Save the form template
  const saveTemplate = () => {
    if (!template.name.trim()) {
      showToast('Template name is required', 'error');
      return;
    }

    if (template.schema.length === 0) {
      showToast('Please add at least one field to the form', 'error');
      return;
    }

    if (onSave) {
      onSave(template);
    }
  };

  // Get field type label
  const getFieldTypeLabel = (type: string) => {
    const fieldType = fieldTypes.find((ft) => ft.value === type);
    return fieldType ? fieldType.label : type;
  };

  // Render the basic info section
  const renderBasicInfoSection = () => {
    return (
      <div className={`p-6 rounded-lg shadow-sm ${isDark ? 'bg-gray-800' : 'bg-white'} border`}>
        <h2 className="text-xl font-semibold mb-4">Basic Information</h2>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="name"
              className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
            >
              Template Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={template.name}
              onChange={handleTemplateChange}
              className={`px-3 py-2 mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Enter template name"
            />
          </div>
          <div>
            <label
              htmlFor="description"
              className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={template.description}
              onChange={handleTemplateChange}
              rows={3}
              className={`px-3 py-2 mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              placeholder="Enter template description"
            />
          </div>
        </div>
      </div>
    );
  };

  // Render the fields section
  const renderFieldsSection = () => {
    return (
      <div
        className={`p-6 rounded-lg shadow-sm h-full ${isDark ? 'bg-gray-800' : 'bg-white'} border`}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Form Fields</h2>
          <button
            onClick={handleAddField}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus size={16} className="mr-1" /> Add Field
          </button>
        </div>

        {template.schema.length === 0 ? (
          <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>No fields added yet. Click "Add Field" to create your first form field.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-md overflow-hidden">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={sortColumn}>
              <SortableContext
                items={template.schema.map((input) => ({
                  id: input.name,
                  ...input
                }))}
                strategy={verticalListSortingStrategy}
              >
                <table
                  className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}
                >
                  <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th />
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}
                      >
                        Label
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}
                      >
                        Type
                      </th>
                      <th
                        scope="col"
                        className={`px-4 py-3 text-left text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-500'} uppercase tracking-wider`}
                      >
                        Required
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody
                    className={
                      isDark
                        ? 'bg-gray-800 divide-y divide-gray-700'
                        : 'bg-white divide-y divide-gray-200'
                    }
                  >
                    {template.schema.map((field, index) => (
                      <FieldRow
                        key={index}
                        field={field}
                        index={index}
                        totalFields={template.schema.length}
                        onFieldChange={handleFieldRowChange}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        onEdit={handleEditField}
                        onDelete={handleDeleteField}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    );
  };

  // Render the field modal
  const renderFieldModal = () => {
    if (!isModalVisible) return null;

    return (
      <Dialog
        open={isModalVisible}
        onOpenChange={(open) => {
          if (!open) {
            handleModalCancel();
          }
        }}
      >
        <DialogContent
          className={`max-w-2xl max-h-[90vh] overflow-y-auto ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
        >
          <DialogHeader>
            <DialogTitle className={theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}>
              {editingFieldIndex !== null ? 'Edit Field' : 'Add Field'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="space-y-4 mb-8">
              <div>
                <label
                  htmlFor="name"
                  className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                >
                  Field Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={currentField.name}
                  onChange={handleFieldChange}
                  className={`px-3 py-2 mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                  }`}
                  placeholder="Enter field name (no spaces)"
                />
                <p
                  className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Use a unique identifier without spaces (e.g., "first_name")
                </p>
              </div>

              <div>
                <label
                  htmlFor="label"
                  className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                >
                  Field Label *
                </label>
                <input
                  type="text"
                  id="label"
                  name="label"
                  value={currentField.label}
                  onChange={handleFieldChange}
                  className={`px-3 py-2 mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                  }`}
                  placeholder="Enter field label"
                />
                <p
                  className={`mt-1 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  This is displayed to users (e.g., "First Name")
                </p>
              </div>

              <div>
                <label
                  htmlFor="type"
                  className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                >
                  Field Type
                </label>
                <select
                  id="type"
                  name="type"
                  value={currentField.type}
                  onChange={handleFieldChange}
                  className={`px-3 py-2 mt-1 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-400'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  }`}
                >
                  {fieldTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {getFieldTypeLabel(type.value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="required"
                  name="required"
                  checked={currentField.required}
                  onChange={handleFieldChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="required"
                  className={`ml-2 block text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                >
                  Required field
                </label>
              </div>

              {/* Options for select/radio/checkbox fields */}
              {['select', 'radio', 'checkbox'].includes(currentField.type) && (
                <div>
                  <h4
                    className={`text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}
                  >
                    Options
                  </h4>

                  {/* List of current options */}
                  <div className="mb-3 max-h-48 pe-1 overflow-y-auto">
                    {!currentField.options?.length && (
                      <p
                        className={`text-sm rounded p-4 ${theme === 'dark' ? 'text-gray-300 bg-gray-700' : 'text-gray-700 bg-gray-100'}`}
                      >
                        No options yet.
                      </p>
                    )}
                    {currentField.options?.map((option, index) => {
                      // Handle both string and object options
                      const optionLabel =
                        typeof option === 'string' ? option : option.label || option.value || '';
                      const optionValue = typeof option === 'string' ? option : option.value || '';

                      return (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-4 rounded mb-2 ${
                            theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                          }`}
                        >
                          <span
                            className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}
                          >
                            {optionLabel} {optionLabel !== optionValue && `(${optionValue})`}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeOption(index)}
                            className={`${
                              theme === 'dark'
                                ? 'text-red-400 hover:text-red-300'
                                : 'text-red-500 hover:text-red-700'
                            }`}
                            aria-label="Remove option"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add new option */}
                  <div className="flex space-x-2 mb-2">
                    <div className="flex-1">
                      <label
                        className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        Label
                      </label>
                      <input
                        type="text"
                        name="label"
                        value={currentOption.label}
                        onChange={handleOptionChange}
                        className={`px-3 py-2 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                        }`}
                        placeholder="Display text"
                      />
                    </div>
                    <div className="flex-1">
                      <label
                        className={`block text-xs mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        Value
                      </label>
                      <input
                        type="text"
                        name="value"
                        value={currentOption.value}
                        onChange={handleOptionChange}
                        className={`px-3 py-2 block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                        }`}
                        placeholder="stored_value"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addOption}
                    className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                      theme === 'dark'
                        ? 'text-white bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                        : 'text-white bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                  >
                    <Plus size={14} className="mr-1" /> Add Option
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleModalCancel}
                className={`px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  theme === 'dark'
                    ? 'text-gray-300 bg-gray-600 hover:bg-gray-500 focus:ring-gray-500 focus:ring-offset-gray-800'
                    : 'text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-gray-500'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveField}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-500 focus:ring-blue-400 focus:ring-offset-gray-800'
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                }`}
              >
                {editingFieldIndex !== null ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Render tabs
  const renderTabs = () => {
    return (
      <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('basic')}
              className={`inline-block py-2 px-4 text-sm font-medium ${
                activeTab === 'basic'
                  ? `border-b-2 border-blue-600 text-blue-600 dark:text-blue-500 dark:border-blue-500`
                  : `border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`
              }`}
            >
              Basic Info
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('fields')}
              className={`inline-block py-2 px-4 text-sm font-medium ${
                activeTab === 'fields'
                  ? `border-b-2 border-blue-600 text-blue-600 dark:text-blue-500 dark:border-blue-500`
                  : `border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 ${
                      isDark ? 'text-gray-300' : 'text-gray-600'
                    }`
              }`}
            >
              Form Fields
            </button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div id="form-template-builder" className="flex flex-col min-h-full h-full gap-4">
      <div className="flex flex-row justify-between items-start gap-8">
        <div className="flex-1">{renderTabs()}</div>
        <button
          onClick={saveTemplate}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <CheckIcon size={16} className="mr-1" /> Save Template
        </button>
      </div>

      <div className="flex-1">
        {activeTab === 'basic' ? renderBasicInfoSection() : renderFieldsSection()}
      </div>

      {renderFieldModal()}
    </div>
  );
};

export default FormTemplateBuilder;
